import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Footer from "./components/footer/footer";
import Header from "./components/header/headerNavBarNew";
// Homepage-ul și articolele (cele mai vizitate) se încarcă direct;
// restul paginilor se descarcă doar când sunt deschise (code splitting).
import Home from "./pages/home/home";
import FourZeroFour from "./pages/404/404";
import ReusableComponentDinamicPages from "./components/reusable-component-grid/reusable-component-dinamic-pages";
import RouteSeo from "./seo/RouteSeo";

// Banner-ul de cookie-uri apare după 2 secunde și Google Analytics doar după acord:
// le descărcăm separat, ca să nu încarce bundle-ul principal al primei pagini.
const ModernCookieConsent = lazy(() => import("./components/cookie-content/cookie-content"));

const Contact = lazy(() => import("./pages/contact/contact"));
const InformatiiUtile = lazy(() => import("./pages/informatiiutile/informatiiutile"));
const B2B = lazy(() => import("./pages/b2b/b2b"));
const IstorieSiArta = lazy(() => import("./pages/istorie-arta/istorie-arta"));
const Plimbare = lazy(() => import("./pages/plimbare/plimbare"));
const Distractie = lazy(() => import("./pages/distractie/distractie"));
const ConditiiDeCalatorie = lazy(() => import("./pages/conditii-de-calatorie/conditiidecalatorie"));
const ComunicatDePresa1 = lazy(() => import("./pages/comunicatdepresa/comunicatdepresa"));
const ComunicatDePresa2 = lazy(() => import("./pages/comunicatdepresa/comunicatdepresa2"));
const ComunicatDePresa3 = lazy(() => import("./pages/comunicatdepresa/comunicatdepresa3"));
const Corporate = lazy(() => import("./pages/corporate/corporate2"));
const Leisure = lazy(() => import("./pages/leisure/leisure2"));
const Services = lazy(() => import("./pages/servicii/servicii"));
const Capabilities = lazy(() => import("./pages/consultanta/capabilitati"));
// import { LanguageProvider } from "./context/languageContext";
const PDFConverter = lazy(() => import("./pages/pdfConverter/pdfConvert"));
const CREATEComponent = lazy(() => import("./components/footer/createPage"));
// import TravelOffer from "./components/last-minute/last-minute";
const TermeniConditiiCardCadou = lazy(() => import("./pages/termeni si conditii Card Cadou/termeni-conditii-card-cadou"));
const Press = lazy(() => import("./pages/presa/presa"));
const Faq = lazy(() => import("./pages/faq/faq"));
const EuProjects = lazy(() => import("./pages/proiecte-europene/proiecte-europene"));

// import pagini continente
const Blog = lazy(() => import("./pages/blog/blog"));
const Romania = lazy(() => import("./pages/paginicontinente/romania"));
const Asia = lazy(() => import("./pages/paginicontinente/asia"));
const Africa = lazy(() => import("./pages/paginicontinente/africa"));
const AmericaDeSud = lazy(() => import("./pages/paginicontinente/americadesud"));
const Europa = lazy(() => import("./pages/paginicontinente/europa"));
const AmericaDeNord = lazy(() => import("./pages/paginicontinente/americadenord"));
const AustraliaSiOceania = lazy(() => import("./pages/paginicontinente/australiasioceania"));

// import pagini vacante
const Revelion = lazy(() => import("./pages/paginiVacante/revelion"));

//Reusable component
const ReusableComponentDinamicPagesVacanteRevelion = lazy(() => import("./components/reusable-component-oferte/reusable-component-dinamic-pages-vacante"));

// Chakra UI 2.5 pune role="region" pe lista de notificări (<ul>), rol nepermis pe <ul>;
// agenții AI și cititoarele de ecran o raportează ca eroare. (Corectat în Chakra 2.10.)
const fixToastRegions = () => {
  document
    .querySelectorAll('ul[id^="chakra-toast-manager-"][role="region"]')
    .forEach((el) => el.removeAttribute("role"));
};

function App() {
  const [openCookieSettings, setOpenCookieSettings] = useState(null);

  const handleRegisterOpen = useCallback((fn) => {
    setOpenCookieSettings(() => fn);
  }, []); // array gol = se creează o singură dată

  const handleOpenCookieSettings = useCallback(() => {
    openCookieSettings?.();
  }, [openCookieSettings]);

  useEffect(() => {
    // Verifică dacă există deja consimțământ salvat din sesiuni anterioare
    const savedConsent = localStorage.getItem("cookieConsent");
    if (savedConsent) {
      const preferences = JSON.parse(savedConsent);
      if (preferences.analytics) {
        initGA();
      }
    }

    // Ascultă consimțământul nou dat în această sesiune
    // Pixelul ChatGPT Ads (inițializat în public/index.html) urmează același acord
    const handleGranted = () => {
      initGA();
      window.oaiq?.("consent", true);
      window.loadOaiqSdk?.();
    };
    const handleDenied = () => {
      // Oprește GA dacă era activ (ex: user revocă din setări)
      // react-ga4 nu are un "disable" built-in, dar poți bloca viitoarele call-uri
      window[`ga-disable-${process.env.React_App_Tracking_ID}`] = true;
      window.oaiq?.("consent", false);
    };

    window.addEventListener("cookieConsentGranted", handleGranted);
    window.addEventListener("cookieConsentDenied", handleDenied);

    return () => {
      window.removeEventListener("cookieConsentGranted", handleGranted);
      window.removeEventListener("cookieConsentDenied", handleDenied);
    };
  }, []);

  useEffect(() => {
    fixToastRegions();
    // Listele de notificări apar în portaluri, după render; paginile cu propriul
    // ChakraProvider (Corporate, Leisure) adaugă altele, deci urmărim continuu.
    const observer = new MutationObserver(fixToastRegions);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const initGA = () => {
    import("react-ga4").then(({ default: ReactGA }) => {
      ReactGA.initialize(process.env.React_App_Tracking_ID);
      ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    });
  };

  return (
    <div className="app">
      <div className="container-app">
        <Router>
          <RouteSeo />
          <Header onOpenCookieSettings={handleOpenCookieSettings} />
          <main>
            {/* minHeight: footer-ul nu urcă cât timp se descarcă pagina */}
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
              <Routes>
                {/* Versiunea în română a paginilor publice (vezi src/i18n/lang.js) */}
                <Route path="/ro" element={<Home />} />
                <Route path="/ro/servicii" element={<Services />} />
                <Route path="/ro/capabilitati" element={<Capabilities />} />
                <Route path="/ro/agentii-partenere" element={<B2B />} />
                <Route path="/ro/contact" element={<Contact />} />
                <Route path="/ro/presa" element={<Press />} />
                <Route path="/ro/faq" element={<Faq />} />

                <Route path="/" element={<Home />}></Route>
                <Route path="/corporate" element={<Corporate />}></Route>
                <Route path="/pdfconverter" element={<PDFConverter />}></Route>
                <Route path="/leisure" element={<Leisure />}></Route>
                <Route path="/servicii" element={<Services />}></Route>
                <Route path="/capabilitati" element={<Capabilities />}></Route>
                <Route
                  path="/termeni-conditii-card-cadou"
                  element={<TermeniConditiiCardCadou />}
                ></Route>
                <Route path="/blog" element={<Blog />}></Route>
                <Route
                  path="/informatii-utile/"
                  element={<InformatiiUtile />}
                ></Route>
                <Route path="/istorie-si-arta/" element={<IstorieSiArta />}></Route>
                <Route path="/distractie/" element={<Distractie />}></Route>
                <Route path="/plimbare/" element={<Plimbare />}></Route>
                <Route path="/agentii-partenere" element={<B2B />}></Route>
                <Route
                  path="/conditiidecalatorie"
                  element={<ConditiiDeCalatorie />}
                ></Route>
                <Route
                  path="/comunicat-de-presa-proiect-lansare"
                  element={<ComunicatDePresa1 />}
                ></Route>
                <Route
                  path="/comunicat-de-presa-proiect-finalizare"
                  element={<ComunicatDePresa2 />}
                ></Route>
                <Route
                  path="/comunicat-finalizare-proiect-ID-131118"
                  element={<ComunicatDePresa3 />}
                ></Route>
                <Route path="/contact" element={<Contact />}></Route>
                <Route path="/presa" element={<Press />} />
                <Route path="/faq" element={<Faq />} />

                <Route path="/create" element={<CREATEComponent />}></Route>
                <Route path="/proiecte-europene" element={<EuProjects />} />

                {/*Rute pagini continente */}
                <Route path="/romania/" element={<Romania />}></Route>
                <Route path="/europa/" element={<Europa />}></Route>
                <Route path="/asia/" element={<Asia />}></Route>
                <Route path="/africa/" element={<Africa />}></Route>
                <Route path="/americadenord/" element={<AmericaDeNord />}></Route>
                <Route path="/americadesud/" element={<AmericaDeSud />}></Route>
                <Route
                  path="/australiasioceania/"
                  element={<AustraliaSiOceania />}
                ></Route>

                {/* Rute Vacante */}
                <Route path="/revelion/" element={<Revelion />}></Route>
                <Route path="/sejururi/" element={<Revelion />}></Route>
                <Route path="/circuite/" element={<Revelion />}></Route>

                {/* Rute Albume */}
                <Route
                  exact
                  path="/:albumId"
                  element={<ReusableComponentDinamicPages />}
                ></Route>
                <Route
                  exact
                  path="/revelion/:albumId"
                  element={<ReusableComponentDinamicPagesVacanteRevelion />}
                ></Route>
                {/* 404 error page routes */}
                <Route path="*" element={<FourZeroFour />}></Route>
              </Routes>
            </Suspense>
          </main>
          <Footer onOpenCookieSettings={handleOpenCookieSettings} />
          <Suspense fallback={null}>
            <ModernCookieConsent onRegisterOpen={handleRegisterOpen} />
          </Suspense>
        </Router>
      </div>
    </div>
  );
}

export default App;
